'use client';

import {
  Button,
  IconOnlyButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
} from '@ctct/summit-core';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@ctct/summit-extended';
import { XIcon } from '@ctct/summit-icons';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useRef } from 'react';
import { type RegisterOptions, useFormContext } from 'react-hook-form';

import {
  type AddressPrediction,
  MIN_SEARCH_LENGTH,
  useAddressAutocompleteField,
  type UseAddressAutocompleteFieldOptions,
} from '../../../hooks';
import { TextInput } from '../../inputs-core';

interface AddressAutocompleteFieldProps extends UseAddressAutocompleteFieldOptions {
  placeholder: string;
  isDisabled: boolean;
  registerOpts?: RegisterOptions;
  dropdownContainer?: HTMLElement | null;
}

const AddressAutocompleteField = ({
  formKey,
  placeholder,
  isDisabled,
  registerOpts,
  onSearchChange,
  dropdownContainer,
  ...hookOptions
}: AddressAutocompleteFieldProps) => {
  const t = useTranslations('UiForm');
  const { watch } = useFormContext();
  const fieldValue = watch(formKey) as string;

  const {
    predictions,
    isOpen,
    setIsOpen,
    isLoading,
    isDebouncePending,
    isLoadingBusinessInfo,
    handleInputChange,
    handleSelect,
    handleEnterManually,
    handlePreventAutoFocus,
    handleClear,
  } = useAddressAutocompleteField({ formKey, onSearchChange, ...hookOptions });

  const commandRef = useRef<HTMLDivElement>(null);

  const emptyText = useMemo(() => {
    if (isLoading || isDebouncePending) return t('shared.addressAutocomplete.searching');
    return null;
  }, [isLoading, isDebouncePending, t]);

  const hasContent = predictions.length > 0 || isLoading || isDebouncePending;
  const shouldShowDropdown =
    isOpen && !!fieldValue && fieldValue.length >= MIN_SEARCH_LENGTH && hasContent;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!shouldShowDropdown) return;
      if (['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) {
        e.preventDefault();
        commandRef.current?.dispatchEvent(
          new KeyboardEvent('keydown', { key: e.key, bubbles: true })
        );
      }
      if (e.key === 'Escape') setIsOpen(false);
    },
    [shouldShowDropdown, setIsOpen]
  );

  const handleClearClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleClear();
    },
    [handleClear]
  );

  const handlePopoverOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) setIsOpen(false);
    },
    [setIsOpen]
  );

  return (
    <Popover open={shouldShowDropdown} onOpenChange={handlePopoverOpenChange}>
      <PopoverTrigger asChild>
        <div className="r:relative">
          <TextInput
            formKey={formKey}
            placeholder={placeholder}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={isDisabled}
            registerOpts={registerOpts}
            shouldDisableAutofill
            endAdornment={
              isLoadingBusinessInfo ? (
                <Spinner className="r:mr-3 r:h-4 r:w-4" />
              ) : (
                fieldValue && (
                  <IconOnlyButton
                    aria-label={t('shared.addressAutocomplete.clearAriaLabel')}
                    emphasis="low"
                    size="xs"
                    onClick={handleClearClick}
                    className="r:mr-1"
                  >
                    <XIcon />
                  </IconOnlyButton>
                )
              )
            }
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="r:w-(--radix-popover-trigger-width) r:p-0"
        align="start"
        side="bottom"
        onOpenAutoFocus={handlePreventAutoFocus}
        container={dropdownContainer}
      >
        <Command ref={commandRef} shouldFilter={false} loop>
          <CommandList>
            {emptyText && (
              <CommandEmpty className="r:text-left r:px-3 r:py-2 r:text-sm r:leading-5">
                {emptyText}
              </CommandEmpty>
            )}
            {predictions.length > 0 && (
              <CommandGroup>
                {predictions.map((prediction: AddressPrediction) => (
                  <CommandItem
                    key={prediction.placeId}
                    value={prediction.placeId}
                    onSelect={() => handleSelect(prediction.placeId)}
                    className="r:text-sm r:leading-5 r:font-normal"
                  >
                    {prediction.description}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
          <div className="r:border-t r:border-neutral-200 r:p-3">
            <Button
              isLinkStyle
              size="sm"
              onClick={handleEnterManually}
              className="r:text-sm r:leading-5 r:text-neutral-600"
              style={{ '--link-underline': 'transparent' }}
            >
              {t('shared.addressAutocomplete.enterManually')}
            </Button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export { AddressAutocompleteField, type AddressAutocompleteFieldProps };
